import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from './api/client';
import * as XLSX from 'xlsx';
import Icons from './components/icons';
import HeaderLogged from './components/header_logged';
import Tabs from './components/Tabs';
import PopupHistory from './components/PopupHistory';
import SmallBagsTab from './components/SmallBagsTab';
import SeedsTab from './components/SeedsTab';
import AddonsTab from './components/AddonsTab';
import AddOrderPopup from './components/AddOrderPopup';
import PlanningFilters from './components/PlanningFilters';
import ChangeView from './components/ChangeView';
import ColumnFilterPopup from './components/ColumnFilterPopup';
import './components/UploadOrdersPopup.css';
import './MaterialOrdersPage.css';
import './PlanToProducePage.css';
import UpdateOrderStatusPopup from './components/UpdateOrderStatusPopup';
import UploadOrdersPopup from './components/UploadOrdersPopup';
import UploadOrderDropdown from './components/UploadOrderDropdown';
import ReferenceGuidePopup from './components/ReferenceGuidePopup';
import { downloadOrderExample } from './utils/orderExamples';

const MaterialOrderPlanning = () => {
  const [activeTab, setActiveTab] = useState('sets');
  const [tableData, setTableData] = useState([]);
  const [inventoryData, setInventoryData] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [planningHistory, setPlanningHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const smallBagsRefreshRef = useRef(null);
  const seedsRefreshRef = useRef(null);
  const [isRounding, setIsRounding] = useState(false);
  const smallBagsRoundRef = useRef(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const addonsRefreshRef = useRef(null);
  const selectedPlanning = 1;
  const [isAddOrderOpen, setIsAddOrderOpen] = useState(false);
  const [currentOrderData, setCurrentOrderData] = useState({ sku: '', year: 0, month_num: 0 });
  const [statuses, setStatuses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [ordersData, setOrdersData] = useState({});
  const [currentView, setCurrentView] = useState('all'); // 'left', 'left-order', 'all'
  const [filters, setFilters] = useState({
    selectedBrands: [],
    selectedCalculations: ['Plan to Produce',],
  });
  const tableRefs = useRef({});
  const fileInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef   = useRef(null);
  const searchDebounceRef = useRef(null);

  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setSearchQuery(val), 250);
  }, []);

  const handleSearchClear = useCallback(() => {
    if (searchInputRef.current) searchInputRef.current.value = '';
    clearTimeout(searchDebounceRef.current);
    setSearchQuery('');
  }, []);
  const [columnFilters, setColumnFilters] = useState({});       // Master Bags
  const [columnFiltersSB, setColumnFiltersSB] = useState({});   // Small Bags
  const [columnFiltersSeeds, setColumnFiltersSeeds] = useState({}); // Seeds
  const [isUploadOrdersOpen, setIsUploadOrdersOpen] = useState(false);
  const [isReferenceGuideOpen, setIsReferenceGuideOpen] = useState(false);
  const [isUpdateOrderStatusOpen, setIsUpdateOrderStatusOpen] = useState(false);
  const [allStatuses, setAllStatuses] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [uploadSuppliers, setUploadSuppliers] = useState([]); // поставщики для выбранного типа в попапах
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Fetch statuses и типы материалов для универсальных попапов
  useEffect(() => {
    const fetchSharedData = async () => {
      try {
        const [statusesRes, typesRes] = await Promise.all([
          api.get('/api/orders_statuses'),
          api.get('/api/material_types'),
        ]);
        setAllStatuses(statusesRes.data);
        setMaterialTypes(typesRes.data);
      } catch (error) {
        console.error('Error fetching statuses/material types:', error);
      }
    };
    fetchSharedData();
  }, []);

  // Когда пользователь выбирает тип материала в попапе — грузим поставщиков
  const handleUploadMaterialTypeChange = useCallback(async (typeID) => {
    if (!typeID) { setUploadSuppliers([]); return; }
    try {
      const res = await api.get(`/api/materialsuppliers_for_type/${typeID}`);
      setUploadSuppliers(res.data);
    } catch (e) {
      console.error('Failed to load suppliers for type', typeID, e);
      setUploadSuppliers([]);
    }
  }, []);

  const handleDownloadSmallBagsExample = () => downloadOrderExample('materials');

  // Stable callbacks for SmallBagsTab to prevent unnecessary re-renders/fetches
  const handleSmallBagsRefresh = useCallback((ref) => { smallBagsRefreshRef.current = ref; }, []);
  const handleSmallBagsRound = useCallback((ref) => { smallBagsRoundRef.current = ref; }, []);
  const handleUploadOrdersClose = useCallback(() => setIsUploadOrdersOpen(false), []);
  const handleUpdateOrderStatusClose = useCallback(() => setIsUpdateOrderStatusOpen(false), []);

  useEffect(() => {
    const fetchStatusesAndSuppliers = async () => {
      try {
        const [statusesRes, suppliersRes] = await Promise.all([
          api.get('/api/orders_statuses'),
          api.get('/api/materialsuppliers_for_type/2')
        ]);
        setStatuses(statusesRes.data);
        setSuppliers(suppliersRes.data);
      } catch (error) {
        console.error('Error fetching statuses and suppliers:', error);
      }
    };
    fetchStatusesAndSuppliers();
  }, []);

  useEffect(() => {
    if (tableData.length > 0) {
      fetchOrdersData();
    }
  }, [tableData]);

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const fetchOrdersData = async () => {
    try {
      const skus = tableData.map(row => row.sku);
      if (!skus.length) return;

      const response = await api.post('/api/get_orders_for_tuples', { skus });

      const enrichedOrders = response.data.map(order => ({
        ...order,
        statusName: statuses.find(s => s.statusID === order.statusID)?.status_name || 'Unknown Status',
        supplierName: suppliers.find(s => s.supplierID === order.supplierID)?.supplierName || 'Unknown Supplier'
      }));

      const ordersMap = {};
      enrichedOrders.forEach(order => {
        if (!ordersMap[order.sku]) ordersMap[order.sku] = [];
        ordersMap[order.sku].push(order);
      });

      setOrdersData(ordersMap);
    } catch (error) {
      console.error('Error fetching orders data:', error);
    }
  };

  const openAddOrderPopup = (sku, year, month_num, setName, order = null) => {
    setCurrentOrderData({ 
      sku, 
      year, 
      month_num, 
      setName,
      initialData: order
    });
    setIsAddOrderOpen(true);
  };

  const closeAddOrderPopup = () => {
    setIsAddOrderOpen(false);
  };

  const handleOrderSave = () => {
    closeAddOrderPopup();
    fetchOrdersData();
  };

  const updatePlanningData = async () => {
    setIsLoading(true);
    try {
      const response = await api.post('/api/update_planning_data');
      console.log('Ответ сервера:', response.data);
      if (smallBagsRefreshRef.current) smallBagsRefreshRef.current();
      if (seedsRefreshRef.current) seedsRefreshRef.current();
      if (addonsRefreshRef.current) addonsRefreshRef.current();
    } catch (error) {
      console.error('Ошибка при обновлении данных:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    updatePlanningData();
    const interval = setInterval(updatePlanningData, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchPlanningHistory = async () => {
    try {
      const response = await api.get('/api/sets_forecast_history');
      const historyData = response.data;
      setPlanningHistory(historyData);
      const savedPlanning = localStorage.getItem('selectedPlanning');
      const isValidPlanning = historyData.some(item => item.recid == savedPlanning);
      if (isValidPlanning) {
      } else if (historyData.length > 0) {
        const lastPlanning = historyData[historyData.length - 1].recid;
        localStorage.setItem('selectedPlanning', JSON.stringify(lastPlanning));
      } else {
        localStorage.removeItem('selectedPlanning');
      }
    } catch (error) {
      console.error('Error fetching planning history:', error);
    }
  };

  // Fetch data with filters applied
  const fetchData = async () => {
    try {
      setTableData([]);
      const params = selectedPlanning ? { planning_id: selectedPlanning } : {};
      const [demandResponse, stockResponse] = await Promise.all([
        api.get('/api/forecast_demand', { params }),
        api.get('/api/masterbagsinventory_stock'),
      ]);
      let fetchedData = demandResponse.data;

      // Apply brand filter
      if (filters.selectedBrands.length > 0) {
        fetchedData = fetchedData.filter(row => filters.selectedBrands.includes(row.brandName));
      }

      setTableData(fetchedData);
      const inventoryMap = stockResponse.data.reduce((acc, item) => {
        acc[item.sku] = item.stock;
        return acc;
      }, {});
      setInventoryData(inventoryMap);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // Update useEffect to refetch data when filters change
  useEffect(() => {
    const loadInitialData = async () => {
      await fetchPlanningHistory();
      if (localStorage.getItem('selectedPlanning')) {
        fetchData();
      }
    };
    loadInitialData();
  }, [filters]);

  useEffect(() => {
    const loadInitialData = async () => {
      await fetchPlanningHistory();
      if (localStorage.getItem('selectedPlanning')) {
        fetchData();
      }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedPlanning) {
      localStorage.setItem('selectedPlanning', JSON.stringify(selectedPlanning));
      fetchData();
    }
  }, [selectedPlanning]);

  useEffect(() => {
    if (selectedPlanning) {
      fetchData();
    }
  }, [selectedPlanning, filters]);

  const handleSelectPlanning = (planningId) => {
    setTableData([]);
    setShowHistory(false);
  };

  const handleDownloadReport = async () => {
    if (!selectedPlanning) {
      alert('Please select a planning first');
      return;
    }
    setIsLoading(true);
    try {
      const [
        masterBagsResponse, smallBagsResponse, seedsResponse, addonsResponse,
        masterStockRes, sbStockRes, sbSeedStockRes, seedsStockRes, seedsPacketRes,
        minitoolsStockRes, silicagelStockRes, seedsProdReturnRes,
      ] = await Promise.all([
        api.get('/api/forecast_demand', { params: { planning_id: selectedPlanning } }),
        api.get('/api/smallbags_forecast', { params: { forecast_id: selectedPlanning } }),
        api.get('/api/seeds_forecast', { params: { forecast_id: selectedPlanning } }),
        api.get('/api/addons_demand', { params: { forecast_id: selectedPlanning } }),
        // inventory
        api.get('/api/masterbagsinventory_stock'),
        api.get('/api/smallbagsinventory_stock'),
        api.get('/api/individualpacketsinventory_monthlyplan', { params: { limit: 1000 } }),
        api.get('/api/seedsinventory_stock'),
        api.get('/api/individualpacketsinventory_monthlyplanseeds'),
        api.get('/api/minitoolsinventory-planning'),
        api.get('/api/silicagelinventory-planning'),
        api.get('/api/seeds_expected_production_return'),
      ]);

      // ── Build inventory maps ─────────────────────────────────────
      const masterInvMap = masterStockRes.data.reduce((acc, i) => { acc[i.sku] = Number(i.stock) || 0; return acc; }, {});
      const sbInvMap = sbStockRes.data.reduce((acc, i) => { acc[i.sku] = Number(i.stock) || 0; return acc; }, {});
      const sbSeedInvMap = (sbSeedStockRes.data?.data || []).reduce((acc, i) => { acc[i.SKU] = Number(i.currentQTY) || 0; return acc; }, {});
      const seedsInvMap = seedsStockRes.data.reduce((acc, i) => {
        acc[String(i.seedsID)] = { Available: Number(i.Available) || 0 };
        return acc;
      }, {});
      const seedsPacketData = Array.isArray(seedsPacketRes.data?.data) ? seedsPacketRes.data.data : [];
      const seedsPacketInvMap = seedsPacketData.reduce((acc, i) => { acc[String(i.seedsID)] = Number(i.currentQTY) || 0; return acc; }, {});
      const seedsMinGramsMap = seedsPacketData.reduce((acc, i) => { acc[String(i.seedsID)] = Number(i.min_gperpacket) || 0; return acc; }, {});
      const seedsProdReturnMap = seedsProdReturnRes.data || {};
      const minitoolsMap = minitoolsStockRes.data.reduce((acc, i) => { acc[i.brandID] = Number(i.QTY) || 0; return acc; }, {});
      const silicagelStock = Number(silicagelStockRes.data?.total) || 0;

      // ── Helpers ─────────────────────────────────────────────────
      const sortMonths = (monthsSet) =>
        Array.from(monthsSet).sort((a, b) => {
          const [aY, aM] = a.split('-');
          const [bY, bM] = b.split('-');
          return aY - bY || aM - bM;
        });
      const monthLabel = (mk) => {
        const [year, month] = mk.split('-');
        return `${monthNames[parseInt(month) - 1]} ${year}`;
      };

      // Build ordersByEffectiveMonth map from raw orders array
      // qtyField: field on the order object to use as qty ('qty' or 'lbqty')
      // skuField: field on the order object to use as sku key ('sku' or 'seedsID')
      const buildOrdersMap = async (skus, qtyField = 'qty', skuField = 'sku') => {
        if (!skus.length) return {};
        const uniqueSkus = [...new Set(skus.map(String))];
        const res = await api.post('/api/get_orders_for_tuples', { skus: uniqueSkus });
        const now = new Date();
        const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const byMonth = {};
        res.data.forEach(order => {
          let mk;
          if (order.statusID === 'BO' && order.bo_follow_up_date) {
            const d = new Date(order.bo_follow_up_date);
            if (!isNaN(d)) mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          } else if (order.est_del_date) {
            const d = new Date(order.est_del_date);
            if (!isNaN(d)) mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          }
          if (!mk) return;
          // Undelivered orders with a past delivery date roll into the current month
          if (mk < currentMonthKey) mk = currentMonthKey;
          const key = String(order[skuField]);
          if (!byMonth[key]) byMonth[key] = {};
          byMonth[key][mk] = (byMonth[key][mk] || 0) + (Number(order[qtyField]) || 0);
        });
        return byMonth;
      };

      // ── 1. MASTER BAGS sheet ─────────────────────────────────────
      // Left   = stock - demand  (demand is CUMULATIVE per month, always subtract from initial stock)
      // Left+O = stock + cumulative_orders - demand
      const masterBagsData = masterBagsResponse.data;
      const masterMonthsSet = new Set();
      masterBagsData.forEach(row =>
        row.monthlyData.forEach(m => masterMonthsSet.add(`${m.year}-${String(m.month_number).padStart(2, '0')}`))
      );
      const sortedMasterMonths = sortMonths(masterMonthsSet);

      const masterSkus = masterBagsData.map(row => row.sku);
      const masterOrders = await buildOrdersMap(masterSkus, 'qty', 'sku');

      const masterHeaders = ['SKU', 'Set Name', 'Stock', 'Percent'];
      sortedMasterMonths.forEach(mk => {
        const lbl = monthLabel(mk);
        masterHeaders.push(
          `${lbl} Plan to Produce`,
          `${lbl} Left (Plan to Produce)`,
          `${lbl} Left+Order (Plan to Produce)`,
        );
      });

      // ── Filter helpers ───────────────────────────────────────────
      const expCmp = (val, op, thr) => {
        switch (op) {
          case '>':  return val > thr;
          case '>=': return val >= thr;
          case '<':  return val < thr;
          case '<=': return val <= thr;
          case '=':  return val === thr;
          default:   return true;
        }
      };
      const expQ = searchQuery.trim().toLowerCase();

      // ── Filter master bags ───────────────────────────────────────
      const activeMasterColFilters = Object.entries(columnFilters).filter(([, f]) => f?.value !== '');
      let filteredMasterData = masterBagsData;
      if (expQ) filteredMasterData = filteredMasterData.filter(r => r.sku?.toLowerCase().includes(expQ) || (r.name || '').toLowerCase().includes(expQ));

      const masterSheetData = filteredMasterData.map(row => {
        const stock = masterInvMap[row.sku] || 0;
        const getVal = (mk, field) => {
          const [y, m] = mk.split('-');
          const md = row.monthlyData.find(d => d.month_number === parseInt(m) && d.year === parseInt(y));
          return Number(md?.[field]) || 0;
        };
        const leftTP  = {};
        const leftOTP = {};
        let cumOrd = 0;
        sortedMasterMonths.forEach(mk => {
          cumOrd += masterOrders[row.sku]?.[mk] || 0;
          leftTP[mk]  = Number((stock - getVal(mk, 'to_produce_quantity')).toFixed(2));
          leftOTP[mk] = Number((stock + cumOrd - getVal(mk, 'to_produce_quantity')).toFixed(0));
        });
        // Apply column filters
        if (activeMasterColFilters.some(([mk, f]) => !expCmp(leftOTP[mk] || 0, f.operator, parseFloat(f.value)))) return null;

        const rowData = [row.sku, row.name || 'N/A', stock, row.percent ? `${row.percent}%` : '0'];
        sortedMasterMonths.forEach(mk => {
          rowData.push(
            getVal(mk, 'to_produce_quantity'),
            leftTP[mk],
            leftOTP[mk],
          );
        });
        return rowData;
      }).filter(Boolean);
      const masterSheet = XLSX.utils.aoa_to_sheet([masterHeaders, ...masterSheetData]);

      // ── 2. SMALL BAGS sheet ──────────────────────────────────────
      // Demand is CUMULATIVE — always subtract from INITIAL stock (seed stock covers first)
      // Left+O: orders are accumulated month-by-month, added to effective seed stock
      const smallBagsRaw = smallBagsResponse.data;
      const sbMap = new Map();
      const sbMonthsSet = new Set();
      smallBagsRaw.forEach(item => {
        if (!item.year || !item.month) return;
        const key = `${item.sbsku}-${item.packName}`;
        if (!sbMap.has(key)) sbMap.set(key, { sbsku: item.sbsku, packName: item.packName, months: {} });
        const mk = `${item.year}-${String(item.month).padStart(2, '0')}`;
        sbMonthsSet.add(mk);
        sbMap.get(key).months[mk] = {
          to_produce: item.total_to_produce_quantity || 0,
        };
      });
      const sortedSbMonths = sortMonths(sbMonthsSet);

      const sbSkus = Array.from(sbMap.values()).map(row => row.sbsku);
      const sbOrders = await buildOrdersMap(sbSkus, 'qty', 'sku');

      const sbHeaders = ['SKU', 'Pack Name', 'Stock'];
      sortedSbMonths.forEach(mk => {
        const lbl = monthLabel(mk);
        sbHeaders.push(
          `${lbl} Plan to Produce`,
          `${lbl} Left (Plan to Produce)`,
          `${lbl} Left+Order (Plan to Produce)`,
        );
      });

      const activeSBColFilters = Object.entries(columnFiltersSB).filter(([, f]) => f?.value !== '');
      let filteredSBRows = Array.from(sbMap.values());
      if (expQ) filteredSBRows = filteredSBRows.filter(r => r.sbsku?.toLowerCase().includes(expQ) || (r.packName || '').toLowerCase().includes(expQ));

      const sbSheetData = filteredSBRows.map(row => {
        const initialSeedStock = sbSeedInvMap[row.sbsku] || 0;
        const initialStock = sbInvMap[row.sbsku] || 0;

        // Exact replication of SmallBagsTab.calculateRollingLeft
        const makeLeft = (type) => {
          const out = {};
          sortedSbMonths.forEach(mk => {
            const demand = Number(row.months[mk]?.[type]) || 0;
            const remainingSeedStock = Math.max(0, initialSeedStock - demand);
            const excessDemand = demand - (initialSeedStock - remainingSeedStock);
            const remainingStock = Number((initialStock - excessDemand).toFixed(2));
            out[mk] = Number((remainingSeedStock + remainingStock).toFixed(2));
          });
          return out;
        };

        // Exact replication of SmallBagsTab.calculateRollingLeftWithOrders
        const makeLeftO = (type) => {
          const out = {};
          let cumulativeOrders = 0;
          sortedSbMonths.forEach(mk => {
            cumulativeOrders += sbOrders[row.sbsku]?.[mk] || 0;
            const demand = Number(row.months[mk]?.[type]) || 0;
            const effectiveSeedStock = initialSeedStock + cumulativeOrders;
            const remainingSeedStock = Math.max(0, effectiveSeedStock - demand);
            const excessDemand = demand - (effectiveSeedStock - remainingSeedStock);
            const remainingStock = Number((initialStock - excessDemand).toFixed(2));
            out[mk] = Number((remainingSeedStock + remainingStock).toFixed(2));
          });
          return out;
        };

        const leftTP = makeLeft('to_produce');
        const leftOTP = makeLeftO('to_produce');

        if (activeSBColFilters.some(([mk, f]) => !expCmp(leftOTP[mk] || 0, f.operator, parseFloat(f.value)))) return null;

        const rowData = [row.sbsku, row.packName || '-', initialStock];
        sortedSbMonths.forEach(mk => {
          const d = row.months[mk] || { to_produce: 0 };
          rowData.push(
            d.to_produce,
            leftTP[mk],
            leftOTP[mk],
          );
        });
        return rowData;
      }).filter(Boolean);
      const smallBagsSheet = XLSX.utils.aoa_to_sheet([sbHeaders, ...sbSheetData]);

      // ── 3. SEEDS sheet ───────────────────────────────────────────
      // Demand is CUMULATIVE. calculateAllDemands converts lb→packets accounting for initial packet stock.
      // Orders use field 'lbqty', key field 'seedsID' (as string)
      const seedsRaw = seedsResponse.data;
      const seedsMap = new Map();
      const seedsMonthsSet = new Set();
      seedsRaw.forEach(item => {
        if (!item.year || !item.month) return;
        const key = `${item.seedsid}-${item.combinedSeedsName}`;
        if (!seedsMap.has(key)) seedsMap.set(key, { seedsid: String(item.seedsid), seedsName: item.combinedSeedsName, months: {} });
        const mk = `${item.year}-${String(item.month).padStart(2, '0')}`;
        seedsMonthsSet.add(mk);
        seedsMap.get(key).months[mk] = {
          to_produce: Number(item.total_to_produce_quantity_lb) || 0,
        };
      });
      const sortedSeedsMonths = sortMonths(seedsMonthsSet);

      const seedsIDsList = Array.from(seedsMap.values()).map(r => r.seedsid);
      let seedsOrders = {};
      if (seedsIDsList.length > 0) {
        const seedsOrdersRes = await api.post('/api/get_seeds_orders_for_tuples', { seedsIDs: seedsIDsList });
        const expCurrentMonthKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
        seedsOrdersRes.data.forEach(order => {
          let mk;
          if (order.statusID === 'BO' && order.bo_follow_up_date) {
            const d = new Date(order.bo_follow_up_date);
            if (!isNaN(d)) mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          } else if (order.est_del_date) {
            const d = new Date(order.est_del_date);
            if (!isNaN(d)) mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          }
          if (!mk || mk < expCurrentMonthKey) return;
          const sid = order.seedsID.toString();
          if (!seedsOrders[sid]) seedsOrders[sid] = {};
          seedsOrders[sid][mk] = (seedsOrders[sid][mk] || 0) + (Number(order.lbqty) || 0);
        });
      }

      const seedsHeaders = ['Seeds ID', 'Seeds Name', 'Stock (lb)', 'Packets Stock'];
      sortedSeedsMonths.forEach(mk => {
        const lbl = monthLabel(mk);
        seedsHeaders.push(
          `${lbl} Plan to Produce (lb)`, `${lbl} Plan to Produce (pkt)`,
          `${lbl} Left (lb)`, `${lbl} Left (pkt)`,
          `${lbl} Left+Order (lb)`, `${lbl} Left+Order (pkt)`,
        );
      });

      const activeSeedsColFilters = Object.entries(columnFiltersSeeds).filter(([, f]) => f?.value !== '');
      let filteredSeedsRows = Array.from(seedsMap.values());
      if (expQ) filteredSeedsRows = filteredSeedsRows.filter(r => String(r.seedsid).toLowerCase().includes(expQ) || (r.seedsName || '').toLowerCase().includes(expQ));

      const seedsSheetData = filteredSeedsRows.map(row => {
        const sid = row.seedsid;
        const prodReturn = seedsProdReturnMap[sid] || {};
        const initialSeedsStock = (Number(seedsInvMap[sid]?.Available) || 0) + (Number(prodReturn.expectedSeedsReturn) || 0);
        const initialPacketsStock = (Number(seedsPacketInvMap[sid]) || 0) + (Number(prodReturn.expectedPackets) || 0);
        const minGrams = Number(seedsMinGramsMap[sid]) || 0;
        const minPounds = minGrams ? minGrams / 453.592 : 0;

        // Replication of SeedsTab.calculateAllDemands
        // initialPackets is CONSTANT — same value used for every month independently
        const computeDemands = () => {
          const demands = {};
          sortedSeedsMonths.forEach(mk => {
            const demandLbOrig = Number(row.months[mk]?.to_produce) || 0;
            if (!minGrams) {
              demands[mk] = { demandInPounds: demandLbOrig, demandInPackets: 0 };
              return;
            }
            const reqPackets = Math.ceil(demandLbOrig / minPounds);
            if (initialPacketsStock >= reqPackets) {
              demands[mk] = { demandInPounds: 0, demandInPackets: reqPackets };
            } else {
              const shortfall = reqPackets - initialPacketsStock;
              demands[mk] = { demandInPounds: Number((shortfall * minPounds).toFixed(2)), demandInPackets: initialPacketsStock };
            }
          });
          return demands;
        };

        const demandsTP = computeDemands();

        // Returns { lb, pkt } per month — mirrors calculateRollingLeft
        const makeLeft = () => {
          const out = {};
          sortedSeedsMonths.forEach(mk => {
            const { demandInPounds, demandInPackets } = demandsTP[mk];
            out[mk] = {
              lb: Number((initialSeedsStock - demandInPounds).toFixed(2)),
              pkt: Math.max(0, initialPacketsStock - demandInPackets),
            };
          });
          return out;
        };

        const makeLeftO = () => {
          const out = {};
          let cumulativeOrdersLbs = 0;
          sortedSeedsMonths.forEach(mk => {
            cumulativeOrdersLbs += seedsOrders[sid]?.[mk] || 0;
            const { demandInPounds, demandInPackets } = demandsTP[mk];
            out[mk] = {
              lb: Number((initialSeedsStock + cumulativeOrdersLbs - demandInPounds).toFixed(2)),
              pkt: Math.max(0, initialPacketsStock - demandInPackets),
            };
          });
          return out;
        };

        const leftTP = makeLeft();
        const leftOTP = makeLeftO();

        if (activeSeedsColFilters.some(([mk, f]) => !expCmp(leftOTP[mk].lb || 0, f.operator, parseFloat(f.value)))) return null;

        const rowData = [row.seedsid, row.seedsName, initialSeedsStock.toFixed(2), initialPacketsStock.toFixed(0)];
        sortedSeedsMonths.forEach(mk => {
          const { demandInPounds, demandInPackets } = demandsTP[mk];
          rowData.push(
            Number(demandInPounds).toFixed(2), demandInPackets,
            leftTP[mk].lb.toFixed(2), leftTP[mk].pkt,
            leftOTP[mk].lb.toFixed(2), leftOTP[mk].pkt,
          );
        });
        return rowData;
      }).filter(Boolean);
      const seedsSheet = XLSX.utils.aoa_to_sheet([seedsHeaders, ...seedsSheetData]);

      // ── 4. ADDONS sheet ──────────────────────────────────────────
      // API returns { OR: { monthKey: {to_produce,fact,plan} }, SP: {...}, DES: {...} } — only to_produce is exported
      // Left   = stock - demand  (demand cumulative, always from initial stock)
      // Left+O = stock + cumulative_orders - demand
      const addonsRawData = addonsResponse.data;
      const addonNames = { OR: 'OR Mini Garden Tools', SP: 'SP Mini Garden Tools', DES: 'Dessicant Bags' };
      const addonsRaw = ['OR', 'SP', 'DES'].map(id => ({
        id,
        name: addonNames[id],
        months: addonsRawData[id] || {},
      }));
      const addonsMonthsSet = new Set();
      addonsRaw.forEach(row => Object.keys(row.months || {}).forEach(mk => addonsMonthsSet.add(mk)));
      const sortedAddonsMonths = sortMonths(addonsMonthsSet);

      const addonsSkus = addonsRaw.map(row => row.id);
      const addonsOrders = await buildOrdersMap(addonsSkus, 'qty', 'sku');

      const getAddonStock = (id) => id === 'DES' ? silicagelStock : (minitoolsMap[id] || 0);

      const addonsHeaders = ['Addon', 'Stock'];
      sortedAddonsMonths.forEach(mk => {
        const lbl = monthLabel(mk);
        addonsHeaders.push(
          `${lbl} Plan to Produce`,
          `${lbl} Left (Plan to Produce)`,
          `${lbl} Left+Order (Plan to Produce)`,
        );
      });

      const addonsSheetData = addonsRaw.map(row => {
        const stock = getAddonStock(row.id);
        // Exact replication of AddonsTab.calculateRollingLeft & calculateRollingLeftWithOrders
        const leftTP = {};
        const leftOTP = {};
        let cumOrd = 0;
        sortedAddonsMonths.forEach(mk => {
          cumOrd += addonsOrders[row.id]?.[mk] || 0;
          leftTP[mk]  = Number((stock - (Number(row.months[mk]?.to_produce) || 0)).toFixed(2));
          leftOTP[mk] = Number((stock + cumOrd - (Number(row.months[mk]?.to_produce) || 0)).toFixed(2));
        });

        const rowData = [row.name || row.id, stock];
        sortedAddonsMonths.forEach(mk => {
          const d = row.months?.[mk] || { to_produce: 0 };
          rowData.push(
            d.to_produce,
            leftTP[mk],
            leftOTP[mk],
          );
        });
        return rowData;
      });
      const addonsSheet = XLSX.utils.aoa_to_sheet([addonsHeaders, ...addonsSheetData]);

      // ── Build workbook ───────────────────────────────────────────
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, masterSheet, 'Master Bags');
      XLSX.utils.book_append_sheet(wb, smallBagsSheet, 'Small Bags');
      XLSX.utils.book_append_sheet(wb, seedsSheet, 'Seeds');
      XLSX.utils.book_append_sheet(wb, addonsSheet, 'Addons');
      XLSX.writeFile(wb, `planning_report_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const generateExampleCSV = () => {
    const csvContent = `SKU,Year,Plan/Fact,January,February,March,April,May,June,July,August,September,October,November,December
  ORSLS_20_08,2026,Fact,-100,-200,0,50,-300,-400,0,0,100,-500,0,0
  ORSLS_20_08,2026,Plan,-150,-250,0,0,-350,-450,0,0,0,-550,0,0`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'forecast_example.csv');
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDoubleClick = (rowIndex, columnKey) => {
    setEditingCell({ rowIndex, columnKey });
  };

  const handleInputChange = (e, rowIndex, columnKey) => {
    const newValue = e.target.value;
    setTableData(prevData => {
      const newData = [...prevData];
      if (columnKey === 'percent') {
        newData[rowIndex][columnKey] = newValue;
      } else if (columnKey.startsWith('month')) {
        const [_, month, year, type] = columnKey.match(/month(\d+)_(\d+)_(\w+)/);
        const index = newData[rowIndex].monthlyData.findIndex(
          m => m.month_number === parseInt(month) && m.year === parseInt(year)
        );
        if (index !== -1) {
          newData[rowIndex].monthlyData[index][`${type}_quantity`] = newValue;
        }
      }
      return newData;
    });
  };

  const handleSave = async (rowIndex, columnKey) => {
    const row = tableData[rowIndex];
    if (columnKey === 'percent') {
      const value = parseFloat(row.percent);
      if (isNaN(value)) {
        alert("Please enter a valid number");
        return;
      }
      try {
        setIsCalculating(true);
        await api.put(`/api/sets_forecast_percent/${row.recid}`, { percent_inc: value, setsku: row.sku });
        await Promise.all([
          fetchData(),
          smallBagsRefreshRef.current?.(),
          seedsRefreshRef.current?.()
        ]);
        setEditingCell(null);
      } catch (error) {
        console.error('Error updating percent:', error);
        // alert('Update failed: ' + error.message);
      } finally {
        setIsCalculating(false);
      }
    } else if (columnKey.startsWith('month')) {
      const [_, month, year, type] = columnKey.match(/month(\d+)_(\d+)_(\w+)/);
      const monthIndex = row.monthlyData.findIndex(
        m => m.month_number === parseInt(month) && m.year === parseInt(year)
      );
      const monthData = row.monthlyData[monthIndex];
      const value = monthData[`${type}_quantity`];
      try {
        if (monthData.forecastrecid) {
          await api.put(`/api/sets_forecast_monthly/${monthData.forecastrecid}`, {
            fact_quantity: parseInt(monthData.fact_quantity) || 0,
            plan_quantity: parseInt(monthData.plan_quantity) || 0,
            set_forecast_id: row.recid,
            month_number: parseInt(month),
            year: parseInt(year),
            [`${type}_quantity`]: parseInt(value) || 0
          });
        } else {
          const response = await api.post('/api/sets_forecast_monthly', {
            set_forecast_id: row.recid,
            month_number: parseInt(month),
            year: parseInt(year),
            fact_quantity: parseInt(monthData.fact_quantity) || 0,
            plan_quantity: parseInt(monthData.plan_quantity) || 0,
            [`${type}_quantity`]: parseInt(value) || 0
          });
          setTableData(prevData => {
            const newData = [...prevData];
            newData[rowIndex].monthlyData[monthIndex] = {
              ...monthData,
              forecastrecid: response.data.forecastrecid,
              [`${type}_quantity`]: value,
            };
            return newData;
          });
        }
        setEditingCell(null);
        await fetchData();
        if (smallBagsRefreshRef.current) smallBagsRefreshRef.current();
        if (seedsRefreshRef.current) seedsRefreshRef.current();
      } catch (error) {
        console.error('Error updating monthly data:', error);
      }
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target.result;
        const firstLine = text.split('\n')[0];
        const requiredHeaders = ['SKU', 'Year', 'Plan/Fact', 
          'January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'
        ];
        if (!requiredHeaders.every(header => firstLine.includes(header))) {
          alert('CSV must contain headers: SKU, Year, Plan/Fact, and month names');
          return;
        }
        const formData = new FormData();
        formData.append('file', file);
        api.post('/api/upload_sets_forecast', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(() => fetchData())
        .catch(error => console.error('Error uploading file:', error));
      };
      reader.readAsText(file);
    }
  };

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const response = await api.get('/api/masterbagsinventory_stock');
        const inventoryMap = response.data.reduce((acc, item) => {
          acc[item.sku] = item.currentQTY;
          return acc;
        }, {});
        setInventoryData(inventoryMap);
      } catch (error) {
        console.error('Error fetching inventory:', error);
      }
    };
    fetchInventory();
  }, []);

  // Calculate rolling left
  const calculateRollingLeft = (monthlyData, sku, type = 'fact') => {
  const monthlyStock = {};
  const currentStock = Number(inventoryData[sku]) || 0;
  let prevLeft = null;

  allMonths.forEach((monthKey) => {
    const [year, month] = monthKey.split('-');
    const monthNum = parseInt(month);
    const monthData = monthlyData.find(
      m => m.month_number === monthNum && m.year === parseInt(year)
    );
    const demand = monthData
      ? Number(monthData[type === 'to_produce' ? 'to_produce_quantity' :
                        type === 'fact' ? 'fact_quantity' : 'plan_quantity']) || 0
      : 0;

    if (!demand && prevLeft !== null) {
      // Month not filled or filled with 0 — carry forward previous month's Left
      monthlyStock[monthKey] = prevLeft;
    } else {
      monthlyStock[monthKey] = Number((currentStock - demand).toFixed(2));
    }

    prevLeft = monthlyStock[monthKey];
  });

  return monthlyStock;
};

  // Compute orders by effective month (for calculations and display)
  const { ordersByEffectiveMonth, ordersByEffectiveMonthList } = useMemo(() => {
    const now = new Date();
    const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const totals = {};
    const lists = {};

    Object.entries(ordersData).forEach(([sku, orders]) => {
      orders.forEach(order => {
        let effectiveMonth;
        if (order.statusID === 'BO' && order.bo_follow_up_date) {
          const d = new Date(order.bo_follow_up_date);
          if (isNaN(d.getTime())) return;
          effectiveMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        } else if (order.est_del_date) {
          const d = new Date(order.est_del_date);
          if (isNaN(d.getTime())) return;
          effectiveMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        } else {
          return;
        }
        // Undelivered orders with a past delivery date roll into the current month
        if (effectiveMonth < currentMonthKey) effectiveMonth = currentMonthKey;

        if (!totals[sku]) totals[sku] = {};
        if (!totals[sku][effectiveMonth]) totals[sku][effectiveMonth] = 0;
        totals[sku][effectiveMonth] += Number(order.qty) || 0;

        if (!lists[sku]) lists[sku] = {};
        if (!lists[sku][effectiveMonth]) lists[sku][effectiveMonth] = [];
        lists[sku][effectiveMonth].push(order);
      });
    });

    return { ordersByEffectiveMonth: totals, ordersByEffectiveMonthList: lists };
  }, [ordersData]);


  const calculateRollingLeftWithOrders = (monthlyData, sku, type, ordersByMonth) => {
  const monthlyStock = {};
  const currentStock = Number(inventoryData[sku]) || 0;
  let cumulativeOrders = 0;
  let prevLeft = null;

  allMonths.forEach((monthKey) => {
    const [year, month] = monthKey.split('-');
    const monthNum = parseInt(month);
    const ordersThisMonth = ordersByMonth[sku]?.[monthKey] || 0;
    cumulativeOrders += ordersThisMonth;

    const monthData = monthlyData.find(
      m => m.month_number === monthNum && m.year === parseInt(year)
    );
    const demand = monthData
      ? Number(monthData[type === 'to_produce' ? 'to_produce_quantity' :
                        type === 'fact' ? 'fact_quantity' : 'plan_quantity']) || 0
      : 0;

    if (!demand && ordersThisMonth === 0 && prevLeft !== null) {
      // No plan (or plan is 0) and no new orders — carry forward previous month's Left
      monthlyStock[monthKey] = prevLeft;
    } else {
      monthlyStock[monthKey] = Number((currentStock + cumulativeOrders - demand).toFixed(0));
    }

    prevLeft = monthlyStock[monthKey];
  });

  return monthlyStock;
};

  const getLeftColor = (currentLeft, monthlyData, currentMonthKey, type, sku) => {
    // Если остаток <= 0, возвращаем красный цвет
    if (currentLeft <= 0) return 'bg-red-600 text-white';
  
    // Инициализируем переменные для отслеживания остатка и покрытых месяцев
    let remainingStock = currentLeft;
    let monthsCovered = 0;
  
    // Находим индекс текущего месяца в allMonths
    const currentMonthIndex = allMonths.indexOf(currentMonthKey);
    // Если месяц не найден, возвращаем красный цвет (на случай ошибки)
    if (currentMonthIndex === -1) return 'bg-red-600 text-white';
  
    // Проверяем все будущие месяцы из allMonths
    for (let i = currentMonthIndex + 1; i < allMonths.length; i++) {
      const monthKey = allMonths[i];
      // Получаем спрос для месяца; если данных нет, спрос = 0
      const monthData = monthlyData[monthKey]?.[sku];
      const demand = monthData
        ? Number(monthData[type === 'to_produce' ? 'to_produce_quantity' :
                          type === 'fact' ? 'fact_quantity' : 'plan_quantity']) || 0
        : 0;
  
      // Если остаток покрывает спрос, увеличиваем счетчик покрытых месяцев
      if (remainingStock >= demand) {
        remainingStock -= demand;
        monthsCovered++;
      } else {
        // Если остаток недостаточен, прерываем цикл
        break;
      }
    }
  
    // Определяем цвет на основе количества покрытых месяцев
    if (monthsCovered >= 3) return 'bg-green-600 text-white'; // Зеленый: покрывает ≥ 3 месяцев
    if (monthsCovered >= 1) return 'bg-yellow-500 text-white'; // Желтый: покрывает 1–2 месяца
    return 'bg-orange-500 text-white'; // Оранжевый: не покрывает ни одного месяца
  };

// Compute allMonths based on current date
const allMonths = useMemo(() => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // 1-12
  const months = [];

  // Add months of the current year
  for (let month = currentMonth; month <= 12; month++) {
    months.push(`${currentYear}-${String(month).padStart(2, '0')}`);
  }

  // Add months of the next year up to current month
  for (let month = 1; month <= currentMonth; month++) {
    months.push(`${currentYear + 1}-${String(month).padStart(2, '0')}`);
  }

  return months;
}, []); // Empty dependency array since it only depends on current date at mount

const compareOp = (val, op, threshold) => {
  switch (op) {
    case '>':  return val > threshold;
    case '>=': return val >= threshold;
    case '<':  return val < threshold;
    case '<=': return val <= threshold;
    case '=':  return val === threshold;
    default:   return true;
  }
};

const filteredTableData = useMemo(() => {
  let data = tableData;
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase().trim();
    data = data.filter(row =>
      row.sku?.toLowerCase().includes(q) ||
      row.name?.toLowerCase().includes(q)
    );
  }
  const activeColFilters = Object.entries(columnFilters).filter(([, f]) => f?.value !== '');
  if (activeColFilters.length > 0) {
    data = data.filter(row => {
      const leftOrderData = calculateRollingLeftWithOrders(row.monthlyData, row.sku, 'to_produce', ordersByEffectiveMonth);
      return activeColFilters.every(([mk, f]) =>
        compareOp(Number(leftOrderData[mk]) || 0, f.operator, parseFloat(f.value))
      );
    });
  }
  return data;
}, [tableData, searchQuery, columnFilters, ordersByEffectiveMonth, allMonths]);

const renderTable = () => {
  if (filteredTableData.length === 0) return <div>No data available</div>;

  allMonths.sort((a, b) => {
    const [aYear, aMonth] = a.split('-');
    const [bYear, bMonth] = b.split('-');
    return aYear - bYear || aMonth - bMonth;
  });

  const allMonthsData = tableData.reduce((acc, row) => {
    row.monthlyData.forEach(m => {
      const key = `${m.year}-${String(m.month_number).padStart(2, '0')}`;
      acc[key] = acc[key] || {};
      acc[key][row.sku] = m;
    });
    return acc;
  }, {});

  return (
    <div className="table-wrapper">
      <table className="table-planning">
        <thead>
          <tr className="bg-gray-100">
            <th rowSpan="2" className="border border-gray-300 p-2">№</th>
            <th rowSpan="2" className="border border-gray-300 p-2">Set Name</th>
            <th rowSpan="2" className="border border-gray-300 p-2">Stock</th>
            <th rowSpan="2" className="border border-gray-300 p-2">Percent</th>
            {allMonths.map(monthKey => {
              const [year, month] = monthKey.split('-');
              const monthName = monthNames[parseInt(month) - 1];
              // Set colSpan based on currentView
              const colSpan = currentView === 'all' ? 4 : 1;
              return (
                <th key={monthKey} colSpan={colSpan} className="border border-gray-300 p-2">
                  {`${monthName} ${year}`}
                </th>
              );
            })}
          </tr>
          <tr className="bg-gray-50">
            {allMonths.map(monthKey => (
              <React.Fragment key={monthKey}>
                {currentView === 'all' && (
                  <>
                    <th className="border border-gray-300 p-2" title="Demand">Demand</th>
                    <th className="border border-gray-300 p-2" title="Left">Left</th>
                    <th className="border border-gray-300 p-2" title="Left + Order" style={{ whiteSpace: 'nowrap' }}>
                      Left + Order
                      <ColumnFilterPopup
                        filter={columnFilters[monthKey]}
                        onChange={f => setColumnFilters(prev => ({ ...prev, [monthKey]: f }))}
                        onClear={() => setColumnFilters(prev => { const n = { ...prev }; delete n[monthKey]; return n; })}
                      />
                    </th>
                    <th className="border border-gray-300 p-2" title="Order">Order</th>
                  </>
                )}
                {currentView === 'left' && (
                  <th className="border border-gray-300 p-2" title="Left">Left</th>
                )}
                {currentView === 'left-order' && (
                  <th className="border border-gray-300 p-2" style={{ whiteSpace: 'nowrap' }}>
                    Left + Order
                    <ColumnFilterPopup
                      filter={columnFilters[monthKey]}
                      onChange={f => setColumnFilters(prev => ({ ...prev, [monthKey]: f }))}
                      onClear={() => setColumnFilters(prev => { const n = { ...prev }; delete n[monthKey]; return n; })}
                    />
                  </th>
                )}
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredTableData.map((row, rowIndex) => {
            const stock = inventoryData[row.sku] || 0;
            const rollingToProduceLeft = calculateRollingLeft(row.monthlyData, row.sku, 'to_produce');
            const rollingToProduceLeftWithOrders = calculateRollingLeftWithOrders(row.monthlyData, row.sku, 'to_produce', ordersByEffectiveMonth);
            return (
              <tr key={row.recid} className="hover:bg-gray-50">
                <td className="border border-gray-300 p-2">{rowIndex + 1}</td>
                <td className="border border-gray-300 p-2">{row.name || 'N/A'}</td>
                <td className="border border-gray-300 p-2">{stock}</td>
                <td
                  className="border border-gray-300 p-2"
                  onDoubleClick={() => handleDoubleClick(rowIndex, 'percent')}
                >
                  {editingCell?.rowIndex === rowIndex && editingCell?.columnKey === 'percent' ? (
                    <input
                      type="number"
                      value={row.percent || ''}
                      onChange={(e) => handleInputChange(e, rowIndex, 'percent')}
                      onBlur={() => handleSave(rowIndex, 'percent')}
                      onKeyPress={(e) => e.key === 'Enter' && handleSave(rowIndex, 'percent')}
                      autoFocus
                    />
                  ) : (
                    row.percent ? `${row.percent}%` : '0'
                  )}
                </td>
                {allMonths.map((monthKey) => {
                  const [year, month] = monthKey.split('-');
                  const monthNum = parseInt(month, 10);
                  const monthData = row.monthlyData.find(
                    (m) => m.month_number === monthNum && m.year === parseInt(year)
                  ) || { to_produce_quantity: 0 };

                  const toProduceDemand = monthData.to_produce_quantity || 0;
                  const toProduceLeft = rollingToProduceLeft[monthKey] || stock;
                  const toProduceLeftWithOrders = rollingToProduceLeftWithOrders[monthKey] || stock;
                  const toProduceColor = getLeftColor(toProduceLeft, allMonthsData, monthKey, 'to_produce', row.sku);
                  const toProduceColorWithOrders = getLeftColor(toProduceLeftWithOrders, allMonthsData, monthKey, 'to_produce', row.sku);

                  return (
                    <React.Fragment key={monthKey}>
                      {currentView === 'all' && (
                        <>
                          <td className="border border-gray-300 p-2">
                            {filters.selectedCalculations.includes('Plan to Produce') && (
                              <div className="demand-value">{toProduceDemand}</div>
                            )}
                          </td>
                          <td className="border border-gray-300 p-2">
                            {filters.selectedCalculations.includes('Plan to Produce') && (
                              <div className={`left-value ${toProduceColor}`}>{toProduceLeft}</div>
                            )}
                          </td>
                          <td className="border border-gray-300 p-2">
                            {filters.selectedCalculations.includes('Plan to Produce') && (
                              <div className={`left-value ${toProduceColorWithOrders}`}>{toProduceLeftWithOrders.toFixed(0)}</div>
                            )}
                          </td>
                          <td className="border border-gray-300 p-2">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                              {(ordersByEffectiveMonthList[row.sku]?.[monthKey] || []).map((ord, i) => {
                                const accent = { S: '#16a34a', OP: '#059669', BO: '#ea580c', C: '#6b7280' }[ord.statusID] || '#6b7280';
                                return (
                                  <div key={ord.id ?? i}
                                    className="text-xs cursor-pointer"
                                    onClick={() => openAddOrderPopup(row.sku, parseInt(year), monthNum, row.name, ord)}
                                    style={{ borderLeft: `3px solid ${accent}`, background: '#f8fafc', borderRadius: '3px', padding: '2px 5px', lineHeight: '1.4' }}>
                                    <div style={{ fontWeight: 600 }}>{ord.qty}</div>
                                    <div style={{ color: accent }}>{ord.statusName}</div>
                                  </div>
                                );
                              })}
                              <button
                                className="planning-btn-add-order"
                                onClick={() => openAddOrderPopup(row.sku, parseInt(year), monthNum, row.name)}
                              >
                                +
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                      {currentView === 'left' && (
                        <td className="border border-gray-300 p-2">
                          {filters.selectedCalculations.includes('Plan to Produce') && (
                            <div className={`left-value ${toProduceColor}`}>{toProduceLeft}</div>
                          )}
                        </td>
                      )}
                      {currentView === 'left-order' && (
                        <td className="border border-gray-300 p-2">
                          {filters.selectedCalculations.includes('Plan to Produce') && (
                            <div className={`left-value ${toProduceColorWithOrders}`}>{toProduceLeftWithOrders.toFixed(0)}</div>
                          )}
                        </td>
                      )}
                    </React.Fragment>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

  return (
    <>
      {isCalculating && (
        <div className="preloader-overlay">
          <div className="preloader">
            <div className="spinner"></div>
            <p>Updating data...</p>
          </div>
        </div>
      )}
      <Icons />
      <HeaderLogged />
      <div className="container-big">
        {/* Unified toolbar */}
        <div className="mo-toolbar" style={{ marginTop: 108 }}>

          {/* Search — uncontrolled input, debounced filter */}
          <div className="mo-search-wrapper" style={{ flex: '0 0 260px' }}>
            <i className="fas fa-search mo-search-icon"></i>
            <input
              ref={searchInputRef}
              type="text"
              className="mo-search-input"
              placeholder="Search by SKU, name or seeds ID…"
              defaultValue=""
              onChange={handleSearchChange}
            />
            {searchQuery && (
              <button className="mo-search-clear" onClick={handleSearchClear}>
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>

          {/* Clear search */}
          {searchQuery && (
            <button
              onClick={handleSearchClear}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#5B965D', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <i className="fas fa-times-circle"></i> Clear
            </button>
          )}

          {/* Right-side actions */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept=".csv"
              onChange={handleFileUpload}
            />
            
            <button
              className="btn btn__green mr-5"
              onClick={updatePlanningData}
              disabled={isLoading}
            >
              {isLoading ? 'Updating...' : 'Refresh Planning Data'}
            </button>
            <button
              className="btn btn__white_g"
              onClick={handleDownloadReport}
              disabled={isLoading}
            >
              {isLoading ? 'Generating...' : 'Download Report'}
            </button>
            <UploadOrderDropdown
              onAddSmallBagsOrder={() => setIsUploadOrdersOpen(true)}
              onDownloadExample={handleDownloadSmallBagsExample}
              onReferenceGuide={() => setIsReferenceGuideOpen(true)}
              onUpdateOrderStatus={() => setIsUpdateOrderStatusOpen(true)}
            />
            <PlanningFilters onFilterChange={handleFilterChange} />
            <ChangeView onViewChange={setCurrentView} currentView={currentView} />
          </div>
        </div>

        {showHistory && (
          <PopupHistory
            data={planningHistory}
            onSelect={handleSelectPlanning}
            onClose={() => setShowHistory(false)}
            refreshHistory={fetchPlanningHistory}
          />
        )}

        <Tabs
          key={selectedPlanning}
          tabs={[
            { name: 'sets', label: 'Master Bags', content: renderTable() },
            {
              name: 'small_packets',
              label: 'Small Bags',
              content: (
                <SmallBagsTab
                  selectedPlanning={selectedPlanning}
                  onRefresh={handleSmallBagsRefresh}
                  onRoundRef={handleSmallBagsRound}
                  setIsRounding={setIsRounding}
                  filters={filters}
                  currentView={currentView}
                  searchQuery={searchQuery}
                  columnFilters={columnFiltersSB}
                  setColumnFilters={setColumnFiltersSB}
                />
              )
            },
            {
              name: 'seeds',
              label: 'Seeds',
              content: <SeedsTab selectedPlanning={selectedPlanning} onRefresh={ref => (seedsRefreshRef.current = ref)} filters={filters} currentView={currentView} searchQuery={searchQuery} columnFilters={columnFiltersSeeds} setColumnFilters={setColumnFiltersSeeds}/>
            },
            {
              name: 'addons',
              label: 'Addons',
              content: <AddonsTab selectedPlanning={selectedPlanning} onRefresh={ref => (addonsRefreshRef.current = ref)} filters={filters} currentView={currentView}/>
            }
          ]}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tableRefs={tableRefs}
          className="material-order-tabs"
        />
        {isAddOrderOpen && (
          <AddOrderPopup
            sku={currentOrderData.sku}
            year={currentOrderData.year}
            month_num={currentOrderData.month_num}
            setName={currentOrderData.setName}
            statuses={statuses}
            suppliers={suppliers}
            onClose={closeAddOrderPopup}
            onSave={handleOrderSave}
            initialData={currentOrderData.initialData}
          />
        )}
        {/* Upload Orders popup (universal) */}
        {isUploadOrdersOpen && (
          <UploadOrdersPopup
            materialTypes={materialTypes}
            suppliers={uploadSuppliers}
            onMaterialTypeChange={handleUploadMaterialTypeChange}
            onClose={() => { setIsUploadOrdersOpen(false); setUploadSuppliers([]); }}
            onSuccess={() => {
              setIsUploadOrdersOpen(false);
              if (smallBagsRefreshRef.current) smallBagsRefreshRef.current();
              fetchData();
            }}
          />
        )}
        {/* Update Order Status popup (universal) */}
        {isUpdateOrderStatusOpen && (
          <UpdateOrderStatusPopup
            materialTypes={materialTypes}
            statuses={allStatuses}
            onClose={handleUpdateOrderStatusClose}
            onSuccess={() => {
              if (smallBagsRefreshRef.current) smallBagsRefreshRef.current();
              fetchData();
            }}
          />
        )}
        {/* Reference Guide popup — dynamic, all material types */}
        {isReferenceGuideOpen && (
          <ReferenceGuidePopup
            materialTypes={materialTypes}
            statuses={allStatuses}
            onClose={() => setIsReferenceGuideOpen(false)}
          />
        )}
      </div>
    </>
  );
};

export default MaterialOrderPlanning;